import type { RightyPrompt } from '../types'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

/**
 * Default fallback prompts shown when no contextual prompts are available
 */
export const defaultPrompts: RightyPrompt[] = [
  {
    label: 'How can I help?',
    drawerLabel: 'What can Righty help with?',
    prompt: 'What can you help me with on this page?'
  },
  {
    label: 'Quick overview',
    drawerLabel: 'Give me a quick overview',
    prompt: 'Give me a quick overview of what I\'m looking at and what I can do here.'
  },
  {
    label: 'Best practices',
    drawerLabel: 'What are the best practices here?',
    prompt: 'What are the best practices for using this feature effectively?'
  },
  {
    label: 'Get started',
    drawerLabel: 'Help me get started',
    prompt: 'Help me get started with the most important thing I should do first.'
  }
]

/**
 * Merges prompts from multiple sources with priority ordering.
 * Priority: dynamic > route > defaults
 *
 * @param dynamicPrompts - Prompts registered by current component (highest priority)
 * @param routePrompts - Prompts from route meta
 * @param maxCount - Maximum number of prompts to return (default: 4 for drawer)
 * @returns Merged array of prompts, limited to maxCount
 */
export function mergePrompts(
  dynamicPrompts: RightyPrompt[],
  routePrompts: RightyPrompt[],
  maxCount: number = 4
): RightyPrompt[] {
  // Start with dynamic prompts (highest priority)
  const merged: RightyPrompt[] = [...dynamicPrompts]

  // Add route prompts that don't duplicate dynamic prompt labels
  for (const routePrompt of routePrompts) {
    if (!merged.some(p => p.label === routePrompt.label)) {
      merged.push(routePrompt)
    }
  }

  // If we still need more prompts, pad with defaults
  if (merged.length < maxCount) {
    for (const defaultPrompt of defaultPrompts) {
      if (!merged.some(p => p.label === defaultPrompt.label)) {
        merged.push(defaultPrompt)
      }
      if (merged.length >= maxCount) break
    }
  }

  // Return limited count
  return merged.slice(0, maxCount)
}

/**
 * Extracts route prompts from the current route's meta
 *
 * @param route - The current route object
 * @returns Array of prompts from route meta, or empty array
 */
export function getRoutePrompts(route: RouteLocationNormalizedLoaded): RightyPrompt[] {
  return (route.meta?.rightyPrompts as RightyPrompt[]) || []
}

/**
 * Gets contextual suggestions for the drawer.
 * Always returns exactly 4 prompts for consistent UI.
 *
 * @param dynamicPrompts - Prompts registered by current component
 * @param route - The current route object
 * @returns Array of exactly 4 prompts for the drawer
 */
export function getDrawerSuggestions(
  dynamicPrompts: RightyPrompt[],
  route: RouteLocationNormalizedLoaded
): RightyPrompt[] {
  const routePrompts = getRoutePrompts(route)
  return mergePrompts(dynamicPrompts, routePrompts, 4)
}

/**
 * Gets contextual prompts for the external chips (above the trigger button).
 * Returns up to 3 prompts, but only if there are actual contextual prompts.
 * Does NOT include defaults for external chips (they should be meaningful).
 *
 * @param dynamicPrompts - Prompts registered by current component
 * @param route - The current route object
 * @returns Array of up to 3 prompts, or empty if no contextual prompts
 */
export function getExternalChipPrompts(
  dynamicPrompts: RightyPrompt[],
  route: RouteLocationNormalizedLoaded
): RightyPrompt[] {
  const routePrompts = getRoutePrompts(route)

  // Start with dynamic prompts (highest priority)
  const merged: RightyPrompt[] = [...dynamicPrompts]

  // Add route prompts that don't duplicate
  for (const routePrompt of routePrompts) {
    if (!merged.some(p => p.label === routePrompt.label)) {
      merged.push(routePrompt)
    }
  }

  // Return up to 3, but don't pad with defaults for external chips
  return merged.slice(0, 3)
}
